import { AxiosError } from "axios";

import api from "services/api";

import { ICnpjHealth, IPortfolioSummary, IWorkspaceTimeline } from "./contracts/types";
import { IPanelHome } from "./contracts/home.types";
import { IRondaStatus } from "./contracts/ronda.types";
import { IMailStatus } from "./contracts/mail.types";
import { buildPortfolioFixture, kliniTimeline } from "./contracts/fixtures";
const USE_MOCK_FOR_REVIEW = false;

/**
 * Camada de serviço sobre a API real do backend (Slice F5). Contrato de
 * retorno é o corpo direto da resposta HTTP, sem envelope `{ data: ... }`
 * (confirmado em `services/api.ts` — sem interceptor de unwrap).
 */

let simulateSourceUnavailable = false;

export function setSimulateSourceUnavailable(value: boolean): void {
  simulateSourceUnavailable = value;
}

export function isSourceUnavailableSimulationSupported(): boolean {
  return process.env.NODE_ENV !== "production";
}

export function isSourceUnavailableSimulationEnabled(): boolean {
  return simulateSourceUnavailable;
}

function assertSourceAvailable(): void {
  if (simulateSourceUnavailable) {
    throw new Error("Fonte indisponível (simulado)");
  }
}

/** 404 nestes endpoints é "não encontrado", não falha de fonte — vira `undefined` em vez de propagar pro estado de erro da tela. */
function isNotFound(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 404;
}

export async function getPortfolio(): Promise<IPortfolioSummary> {
  assertSourceAvailable();
  if (USE_MOCK_FOR_REVIEW) return buildPortfolioFixture();
  const { data } = await api.get<IPortfolioSummary>("/v2/operational-panel/portfolio");
  return data;
}

/**
 * `GET /v2/operational-panel/home` — as zonas da tela inicial v2.1 (falhas,
 * parados e confiabilidade, unificadas por CNPJ em `IPanelHomeCnpj.state`).
 *
 * `getPortfolio` continua existindo e não foi tocada: o backend serve as duas
 * rotas, e remover a antiga é tarefa separada, depois desta tela estar em
 * produção.
 */
export async function getPanelHome(): Promise<IPanelHome> {
  assertSourceAvailable();
  const { data } = await api.get<IPanelHome>("/v2/operational-panel/home");
  return data;
}

/**
 * `GET /v2/operational-panel/ronda` — estado da Ronda de Invariantes
 * (`04_agent_operations/ronda_invariantes/`, Drive): exceções abertas +
 * timestamp da última rodada. Seção independente de `getPanelHome` — não
 * faz parte do union `CnpjState`, ver `home/RondaSection.tsx`.
 */
export async function getRondaStatus(): Promise<IRondaStatus> {
  assertSourceAvailable();
  const { data } = await api.get<IRondaStatus>("/v2/operational-panel/ronda");
  return data;
}

/**
 * why: `GET /v2/operational-panel/mail` — e-mails das ultimas 24 h (spec
 * 2026-09-21-sinal-emails-falhados). Seccao independente, como a Ronda.
 */
export async function getMailStatus(federalTaxId?: string): Promise<IMailStatus> {
  assertSourceAvailable();
  const { data } = await api.get<IMailStatus>("/v2/operational-panel/mail", {
    params: federalTaxId ? { federalTaxId } : undefined,
  });
  return data;
}

export async function getCnpj(federalTaxId: string): Promise<ICnpjHealth | undefined> {
  assertSourceAvailable();
  if (USE_MOCK_FOR_REVIEW) {
    return buildPortfolioFixture().cnpjs.find((cnpj) => cnpj.federalTaxId === federalTaxId);
  }
  try {
    const { data } = await api.get<ICnpjHealth>(`/v2/operational-panel/cnpj/${federalTaxId}`);
    return data;
  } catch (error) {
    if (isNotFound(error)) return undefined;
    throw error;
  }
}

/**
 * `activityId` sintético (`governance:${workspaceId}`, `export-gap:${workspaceId}`,
 * `qbem-unavailable:${workspaceId}`) não tem execução própria — o backend
 * responde 404, tratado aqui como "sem linha do tempo" (`undefined`), não
 * como erro de fonte.
 */
export async function getWorkspaceTimeline(workspaceId: string, activityId: string): Promise<IWorkspaceTimeline | undefined> {
  assertSourceAvailable();
  if (USE_MOCK_FOR_REVIEW) {
    return workspaceId === kliniTimeline.workspaceId && activityId === kliniTimeline.activityId ? kliniTimeline : undefined;
  }
  try {
    const { data } = await api.get<IWorkspaceTimeline>(`/v2/operational-panel/workspaces/${workspaceId}`, {
      params: { activity_id: activityId },
    });
    return data;
  } catch (error) {
    if (isNotFound(error)) return undefined;
    throw error;
  }
}
